import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table_v3";

import { Button } from "components/ui/button_v3";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select_v4";

import {
  Pencil,
  Trash2,
  Users,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import localization from "services/localization";

export function DepartmentTable({ departments, users, onEdit, onDelete }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getTeacherName = (id) => {
    const user = users.find((u) => u.id === id);
    return user?.name || "Unknown";
  };

  const totalPages = Math.max(1, Math.ceil(departments.length / pageSize));

  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedDepartments = useMemo(() => {
    return departments.slice(
      (safeCurrentPage - 1) * pageSize,
      safeCurrentPage * pageSize,
    );
  }, [departments, pageSize, safeCurrentPage]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col flex-1 min-h-0">
        <div className="max-h-[50vh] overflow-y-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{localization.name || "Name"}</TableHead>
                <TableHead>{localization.owner || "Owner"}</TableHead>
                <TableHead>{localization.type || "Type"}</TableHead>
                <TableHead>{localization.members || "Members"}</TableHead>
                <TableHead>{localization.start_date || "Start Date"}</TableHead>
                <TableHead>{localization.end_date || "End Date"}</TableHead>
                <TableHead className="text-right">
                  {localization.actions || "Actions"}
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {paginatedDepartments.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="h-32 text-center text-muted-foreground"
                  >
                    {localization.no_departments_found ||
                      "No departments found."}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedDepartments.map((department) => (
                  <TableRow key={department.id}>
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {department.name}
                    </TableCell>

                    <TableCell>{getTeacherName(department.user_id)}</TableCell>

                    <TableCell className="capitalize">
                      {department.type}
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users size={16} color="var(--sub-icon)" />
                        {department.members?.length || 0}
                      </div>
                    </TableCell>

                    <TableCell>{department.start_date}</TableCell>

                    <TableCell>{department.end_date}</TableCell>

                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onEdit(department)}
                        >
                          <Pencil
                            size={16}
                            style={{ color: "var(--primary-action)" }}
                          />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => onDelete(department)}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      {/* Pagination footer */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-muted-foreground px-1">
        <span>
          {departments.length} {localization.departments || "Departments"}
        </span>

        <div className="flex items-center gap-4">
          {/* rows per page */}
          <div className="flex items-center gap-2">
            <span className="text-xs whitespace-nowrap page-subtitle">
              {localization.rows_per_page || "Rows per page"}
            </span>

            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[64px] page-subtitle">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* page indicator */}
          <span className="text-xs tabular-nums whitespace-nowrap page-subtitle">
            {localization.page || "Page"} {safeCurrentPage}{" "}
            {localization.of || "of"} {totalPages}
          </span>

          {/* navigation */}
          <div className="flex items-center gap-1 page-subtitle">
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => setCurrentPage(1)}
              disabled={safeCurrentPage <= 1}
            >
              <ChevronsLeft className="size-4" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={safeCurrentPage <= 1}
            >
              <ChevronLeft className="size-4" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={safeCurrentPage >= totalPages}
            >
              <ChevronRight className="size-4" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => setCurrentPage(totalPages)}
              disabled={safeCurrentPage >= totalPages}
            >
              <ChevronsRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
